import { useCallback, useEffect, useState } from 'react';
import { api, ErreurApi, type BrouillonGuichet, type FileService, type LigneAffaire, type MonBureau } from './api';
import { Icone } from './icones';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MES AFFAIRES — la porte d'entrée du bureau vers le travail réel.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Fondateur, 24/09/2026 : « on doit avoir une porte d'entrée vers les affaires
 * depuis le bureau », « il faut que l'agent connaisse les affaires qu'il est en
 * train de traiter », « c'est complètement non productif ce qui est fait ».
 *
 * ⚠️⚠️ CE N'EST PAS DU CODE QUI MANQUAIT, C'EST UN BRANCHEMENT. Mesuré le
 * 24/09/2026 : `GET /affaires/mon-bureau` existe depuis longtemps, applique la
 * règle du fondateur (« l'agent ne choisit pas son affaire »), distingue même
 * « rien ne m'est affecté » de « la file du service est vide » — et
 * `api.affaires.monBureau()`, déclarée `api.ts:525`, n'avait **AUCUN
 * appelant** dans tout le dossier `frontend/src`. Témoin positif joué :
 * `api.demandes.fiche` rend bien son appelant au même grep. Le bureau du
 * métier affichait 417 173 contrôles de cohérence et pas une affaire.
 *
 * ⚠️ DEUX LISTES, ET ELLES NE DISENT PAS LA MÊME CHOSE. « Mes affaires » est
 * ce qui m'est AFFECTÉ ; « la file de mon service » est ce qui attend un agent.
 * Les fondre remettrait le choix dans les mains de l'agent — et le tri par
 * facilité avec.
 *
 * ⚠️ UN BUREAU VIDE DOIT DIRE POURQUOI. « rien ne m'attend » et « le dispatch
 * ne m'a jamais rien envoyé » s'affichent pareil sur une liste vide. Le service
 * écrit déjà la phrase qui les sépare (`message`) : on la rend telle quelle,
 * jamais un « Aucune affaire » de notre cru.
 */

/** ⚠️ Le mot ACCOMPAGNE la couleur : ces écrans s'impriment, et une part des
 *  agents distingue mal le rouge de l'ambre. Règle des documents d'État du dépôt. */
const PRIORITE: Record<string, { classe: string; mot: string }> = {
  urgente: { classe: 'rouge', mot: 'urgente' },
  haute: { classe: 'ambre', mot: 'haute' },
  normale: { classe: 'bleu', mot: 'normale' },
  basse: { classe: 'gris', mot: 'basse' },
};

function jours(depuis: string | null | undefined): string {
  if (!depuis) return '';
  const j = Math.floor((Date.now() - new Date(depuis).getTime()) / 86_400_000);
  return j <= 0 ? "aujourd'hui" : j === 1 ? 'depuis hier' : `depuis ${j} jours`;
}

function CarteAffaire(
  { a, classe, geste, pourquoi, marque, onOuvrir }:
  {
    a: LigneAffaire; classe: string; geste: string; pourquoi: string;
    marque: string; onOuvrir: () => void;
  },
) {
  return (
    <li className={`bureau-acte ${classe}`}>
      <button className="bureau-acte-ouvrir" onClick={onOuvrir}
              title={`Ouvrir ${a.reference}`}>
        <span className={`bureau-pastille ${classe}`} aria-hidden="true" />
        <span className="bureau-acte-titre">
          {/* ⚠️ Le LIBELLÉ de la procédure, jamais `type_code` : « WF-003 »
              demanderait à l'agent de connaître le catalogue des workflows. */}
          <strong>{a.procedure_libelle}</strong>
          <code>{a.reference}</code>
          <span className="mention">
            {[a.requerant, a.parcelle].filter(Boolean).join(' · ') || 'requérant non résolu'}
          </span>
        </span>
        <span className="bureau-acte-etape">
          <strong>{geste}</strong>
          {/* ⚠️ Le POURQUOI est affiché AVEC le geste, jamais derrière un
              survol : un ordre sans raison se discute, une raison fait agir. */}
          <span className="mention">{pourquoi}</span>
        </span>
        <span className={`etat ${classe}`}>{marque}</span>
      </button>
    </li>
  );
}

export function MesAffaires(
  { onOuvrirAffaire, onReprendreSaisie, modes }:
  {
    onOuvrirAffaire: (reference: string) => void;
    /** Rouvrir le guichet sur une saisie inachevée — voir `SaisiesEnCours`. */
    onReprendreSaisie?: (typeCode: string, parcelle: string | null) => void;
    modes?: string[];
  },
) {
  const [b, setB] = useState<MonBureau | null>(null);
  const [f, setF] = useState<FileService | null>(null);
  const [saisies, setSaisies] = useState<{ brouillons: BrouillonGuichet[]; lecture: string } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      setB(await api.affaires.monBureau());
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String(e));
      return;
    }
    /* ⚠️ LA FILE EST FACULTATIVE, ET SON REFUS N'EST PAS UNE PANNE. Un agent
       sans organisation, ou sans droit de lecture sur la file, reçoit 403 : on
       cache la section, on ne noie pas « mes affaires » sous une erreur qui ne
       la concerne pas. */
    try { setF(await api.affaires.file()); } catch { setF(null); }
    /* ⚠️ MÊME RÈGLE : un compte sans droit de guichet reçoit 403 sur ses
       saisies. On cache la section, on ne noie pas le bureau sous une erreur
       qui ne concerne pas les affaires. */
    try { setSaisies(await api.demandes.brouillons()); } catch { setSaisies(null); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  /**
   * ⚠️⚠️ CE BOUTON NE « PREND » PAS : IL DISTRIBUE. Première version : je l'avais
   * libellé « Prendre la suivante ». Mesuré en production le 24/09/2026 —
   * SYLLA clique, et l'affaire part à DIALLO Mariama. `POST /affaires/attribuer`
   * appelle `attribuerProchaine`, qui choisit l'agent éligible le MOINS CHARGÉ
   * de l'organisation ; ce n'est pas un défaut, c'est la règle du fondateur
   * (« l'agent ne choisit pas son affaire ») — mais un bouton qui dit « je la
   * prends » alors que le service la donne à un collègue est une promesse
   * fausse, et l'agent conclut que son clic n'a rien fait.
   *
   * ⚠️ ON NOMME LE DESTINATAIRE. Le service rend `attribuees[].agent` et ne le
   * disait à personne : « 1 affaire attribuée » laissait chacun croire que
   * c'était à lui.
   */
  async function distribuer(mode: string) {
    setOccupe(true); setMessage(null); setErreur(null);
    try {
      const r = await api.affaires.attribuer(mode, 1);
      const a = (r.attribuees as Array<{ reference?: string; agent?: string }> | undefined)?.[0];
      setMessage(a?.agent
        ? `${a.reference} attribuée à ${a.agent}.`
        : r.message);
      await charger();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String(e));
    } finally { setOccupe(false); }
  }

  if (erreur && !b) return (
    <section className="bureau-actes">
      <header className="bureau-tete"><h3>Mes affaires</h3></header>
      <p className="erreur" role="alert">{erreur}</p>
    </section>
  );
  if (!b) return <section className="bureau-actes"><p className="attente">Ouverture de vos affaires…</p></section>;

  const mesModes = f?.modes ?? b.modes ?? modes ?? [];
  const enFile = f ? Object.entries(f.files).filter(([, l]) => l.length > 0) : [];

  return (
    <>
      <section className="bureau-actes">
        <header className="bureau-tete">
          <h3>Mes affaires</h3>
          <p className={b.aTraiter.length === 0 ? 'mention' : ''}>
            {b.aTraiter.length === 0
              ? b.message
              : `${b.aTraiter.length} affaire(s) vous sont affectées`
                + `${b.aTraiter.filter((a) => a.en_retard).length > 0
                    ? ` · ${b.aTraiter.filter((a) => a.en_retard).length} en retard` : ''}.`
              + " Ce qui est en retard passe devant : trier par date mettrait une affaire finie au-dessus d'une affaire empêchée."}
          </p>
        </header>
        {message && <p className="succes" role="status">{message}</p>}
        {erreur && <p className="erreur" role="alert">{erreur}</p>}

        {b.aTraiter.length > 0 && (
          <ul className="bureau-liste">
            {b.aTraiter.map((a) => {
              const p = PRIORITE[a.priorite] ?? PRIORITE.normale!;
              const classe = a.en_retard ? 'rouge' : p.classe;
              return (
                <CarteAffaire
                  key={a.reference} a={a} classe={classe}
                  geste={a.etape_libelle ?? a.etape_courante}
                  /* ⚠️ La consigne vient de `fon.procedure_etape`, calculée au
                     même endroit pour la fiche et ce bureau. Un écran qui
                     trancherait lui-même afficherait tôt ou tard deux conseils
                     contradictoires sur la même affaire. */
                  pourquoi={a.consigne
                    ?? `Étape « ${a.etape_courante} » — aucune consigne n'est écrite pour cette étape de la procédure.`}
                  marque={a.en_retard ? 'en retard' : a.issue === 'prise_en_charge' ? 'en cours' : p.mot}
                  onOuvrir={() => onOuvrirAffaire(a.reference)}
                />
              );
            })}
          </ul>
        )}
      </section>

      {/* ⚠️ LA FILE EST MONTRÉE MÊME PLEINE D'AFFAIRES QUI NE SONT PAS À MOI :
          c'est la seule façon pour un agent de voir qu'il peut travailler. */}
      {f && (
        <section className="bureau-actes">
          <header className="bureau-tete">
            <h3>La file de {f.service}</h3>
            <p className="mention">
              {f.total === 0
                ? "Aucune affaire n'attend dans votre service."
                : `${f.total} affaire(s) attendent un agent. Distribuer la suivante la confie à l'agent `
                  + `le moins chargé du service — l'agent ne choisit pas son affaire.`}
            </p>
            {mesModes.map((m) => (
              <button key={m} className="bouton discret" disabled={occupe || f.total === 0}
                      title="Le moteur choisit l'agent le moins chargé de votre service — pas forcément vous."
                      onClick={() => void distribuer(m)}>
                {mesModes.length > 1 ? `Distribuer la suivante — ${m}` : 'Distribuer la suivante'}
              </button>
            ))}
          </header>

          {/* ⚠️ `candidats = 0` N'EST PAS « RIEN À FAIRE » : c'est « personne ne
              peut la prendre ». Le service écrit l'alerte, on la rend. */}
          {f.alerte && <p className="erreur" role="alert">{f.alerte}</p>}

          {enFile.length > 0 && (
            <ul className="bureau-liste">
              {enFile.flatMap(([mode, lignes]) => lignes.map((a) => (
                <CarteAffaire
                  key={`${mode}:${a.reference}`} a={a}
                  classe={a.candidats === 0 ? 'rouge' : a.en_retard ? 'ambre' : 'gris'}
                  geste={a.etape_libelle ?? a.etape_courante}
                  pourquoi={a.candidats === 0
                    ? "Aucun agent habilité ne peut la prendre : ce n'est pas une file vide, c'est une file morte."
                    : `${a.candidats} agent(s) peuvent la prendre · en file ${jours(a.entree_file_le)}`}
                  marque={a.candidats === 0 ? 'sans preneur' : 'à prendre'}
                  onOuvrir={() => onOuvrirAffaire(a.reference)}
                />
              )))}
            </ul>
          )}
        </section>
      )}

      {/*
        ═══════════════════════════════════════════════════════════════════
        MES SAISIES EN COURS — ce que j'ai commencé et pas déposé.
        ═══════════════════════════════════════════════════════════════════

        Fondateur, 24/09/2026 : « je viens de saisir une donation incomplète,
        elle n'est pas visible dans mes affaires, alors que l'enregistrement
        est automatique ».

        ⚠️⚠️ UNE SAISIE N'EST PAS UNE AFFAIRE, ET LA SECTION EST SÉPARÉE POUR
        CELA. Une affaire a une référence publique, un circuit, des délais
        opposables ; une saisie n'a rien de tout cela et n'engage personne.
        Les mêler dans une seule liste ferait compter comme dossier ouvert ce
        qui n'est qu'un formulaire à moitié rempli — et l'agent croirait
        qu'un usager attend une réponse.
      */}
      {saisies && saisies.brouillons.length > 0 && (
        <section className="bureau-actes">
          <header className="bureau-tete">
            <h3>Mes saisies en cours</h3>
            <p className="mention">
              {saisies.lecture}
            </p>
          </header>
          <ul className="bureau-liste">
            {saisies.brouillons.map((s2) => (
              <li className="bureau-acte gris" key={s2.cle}>
                <button className="bureau-acte-ouvrir"
                        onClick={() => onReprendreSaisie?.(s2.type_code, s2.parcelle)}
                        disabled={!onReprendreSaisie}
                        title={onReprendreSaisie ? 'Reprendre cette saisie' : ''}>
                  <span className="bureau-pastille gris" aria-hidden="true" />
                  <span className="bureau-acte-titre">
                    <strong>{s2.type_libelle ?? s2.type_code}</strong>
                    <span className="mention">
                      {[s2.resume || 'requérant non encore saisi', s2.parcelle]
                        .filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span className="bureau-acte-etape">
                    <strong>étape {s2.etape} · {s2.champs_remplis} champ(s)</strong>
                    {/* ⚠️ L'EXPIRATION EST DITE. Une saisie qui disparaîtra
                        dans trois jours sans prévenir est une perte de travail
                        annoncée nulle part. */}
                    <span className="mention">
                      modifiée {jours(s2.maj_le) || "à l'instant"} · expire le{' '}
                      {new Date(s2.expire_le).toLocaleDateString('fr-FR')}
                    </span>
                  </span>
                  <span className="etat gris">non déposée</span>
                </button>
                <button className="bouton discret danger jeter-saisie"
                        title="Jeter cette saisie — elle n'a jamais été déposée"
                        onClick={() => { void api.demandes.jeterBrouillon(s2.cle).then(charger); }}>
                  Jeter
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ⚠️ ON DIT QUE LA FILE N'A PAS PU ÊTRE LUE, plutôt que de la taire :
          une section absente se lit « il n'y a pas de file ». */}
      {!f && (
        <p className="mention">
          <Icone nom="alerte" taille={12} /> La file de votre service n'est pas lisible
          depuis ce compte — il n'est rattaché à aucune organisation, ou la lecture
          de la file ne lui est pas accordée.
        </p>
      )}
    </>
  );
}
